import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { useAccount } from '@ibk/shared-auth';
import { selectAppDomain, useAppSelector, useGetProductRelationShips, useHomeProducts } from '@ibk/shared-config';
import { useGetHomePermissions, useInstructionsPermissioning } from '@ibk/shared-permissioning';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { AppDomain } from '@ibk/shared-types';
import { AccountActivationStatus, Breadcrumb, DomainCards, Loading, NavBar, PaymentNavCardsAndCarousel, } from '@ibk/shared-ui';
import { sharedLayoutTranslations } from '../../translations';
import styles from './DigitalAccountNavBarAndNavCardsLayout.module.css';
function DigitalAccountNavBarAndNavCardsLayoutBase(props) {
    const location = useLocation();
    const { t } = useTranslation();
    const { getBillingProduct } = useGetProductRelationShips();
    const productsData = useHomeProducts({ enabled: true });
    const currentDomain = useAppSelector(selectAppDomain);
    const { canApprovePaymentLinks, showApprovePaymentLinks } = useInstructionsPermissioning();
    const { hasBillingPermission } = useGetHomePermissions();
    const { hasActivePJAccount, isLoadingPJAccount } = useAccount();
    const isLoading = isLoadingPJAccount ? isLoadingPJAccount() : true;
    const activeAccount = hasActivePJAccount ? hasActivePJAccount() : false;
    const isPaymentBilletRoute = useMemo(() => {
        return location.pathname === '/conta-pj/pagar/boleto';
    }, [location.pathname]);
    const isPixKeyCreationRoute = useMemo(() => {
        return location.pathname === '/conta-pj/pix/criar';
    }, [location.pathname]);
    const isPixCopyPasteRoute = useMemo(() => {
        return location.pathname === '/conta-pj/pagar/pix-copia-e-cola';
    }, [location.pathname]);
    const isPixTransferRoute = useMemo(() => {
        return location.pathname === '/conta-pj/pix/transferir';
    }, [location.pathname]);
    const isPixAccountPayableRoute = useMemo(() => {
        return location.pathname === '/pagamentos/pix/transferir';
    }, [location.pathname]);
    const isPixEmployeeAccountPayableRoute = useMemo(() => {
        return location.pathname === '/pagamentos/pix/transferir/funcionarios';
    }, [location.pathname]);
    const isPixTransferSuccessRoute = useMemo(() => {
        return location.pathname === '/conta-pj/pix/sucesso';
    }, [location.pathname]);
    const isChargesCreateRoute = useMemo(() => {
        return location.pathname === '/conta-pj/cobrancas/criar';
    }, [location.pathname]);
    const isDepositBilletRoute = useMemo(() => {
        return location.pathname === '/conta-pj/receber/deposito';
    }, [location.pathname]);
    const isChargesPDFRoute = useMemo(() => {
        return location.pathname === '/pagamentos/cobranças/pdf/:slipId';
    }, [location.pathname]);
    const isInstructionsRoute = useMemo(() => {
        return (location.pathname === '/conta-pj/aprovar-pagamentos' || location.pathname === '/conta-pj/minhas-solicitacoes');
    }, [location.pathname]);
    const domainCardItems = useMemo(() => {
        return [
            {
                id: 'conta-pj',
                title: t('domainCards.contaPj.title'),
                iconName: 'digitalAccountFilled',
                subtitle: t('domainCards.contaPj.subtitle'),
                value: '--',
                showProgress: true,
            },
            {
                id: 'contabilidade-digital',
                title: t('domainCards.contabilidadeDigital.title'),
                iconName: 'addBank',
                subtitle: t('domainCards.contabilidadeDigital.inactive'),
                value: '--',
                showProgress: true,
            },
        ];
    }, [t]);
    const navBarItems = useMemo(() => {
        const nav = [
            { title: t('navBar.statement'), route: '/conta-pj' },
            { title: t('navBar.pixArea'), route: '/conta-pj/pix' },
        ];
        if (!getBillingProduct()) {
            nav.push({ title: t('navBar.toReceive'), route: '/conta-pj/receber' });
        }
        if (showApprovePaymentLinks) {
            if (canApprovePaymentLinks) {
                nav.push({ title: t('navBar.approveInstructions'), route: '/conta-pj/aprovar-pagamentos' });
            }
            else {
                nav.push({ title: t('navBar.myInstructions'), route: '/conta-pj/minhas-solicitacoes' });
            }
        }
        return nav;
    }, [canApprovePaymentLinks, getBillingProduct, showApprovePaymentLinks, t]);
    const breadcrumbs = [
        { label: t('navBar.home'), href: '/conta-pj' },
        { label: t('payBoleto'), isActive: true },
    ];
    const pixKeyCreationBreadcrumbs = [
        { label: t('navBar.pixArea'), href: '/conta-pj/pix' },
        { label: t('pix-keys.my-keys'), href: '/conta-pj/pix' },
        { label: t('pix-keys.new-key'), isActive: true },
    ];
    const pixCopyPasteBreadcrumbs = [
        { label: t('navBar.pixArea'), href: '/conta-pj/pix' },
        { label: t('pix-copy-paste'), isActive: true },
    ];
    const pixTransferBreadcrumbs = [
        { label: t('navBar.pixArea'), href: '/conta-pj/pix' },
        { label: t('pix-transfer.new-transfer'), isActive: true },
    ];
    const pixAccountPayableBreadcrumbs = [
        { label: t('payments'), href: '/pagamentos' },
        { label: t('pix-transfer.new-transfer'), isActive: true },
    ];
    const pixEmployeeAccountPayableBreadcrumbs = [
        { label: t('payments'), href: '/pagamentos' },
        { label: t('pix-transfer.new-transfer'), isActive: true },
    ];
    const pixTransferSuccessBreadcrumbs = [
        { label: t('navBar.pixArea'), href: '/conta-pj/pix' },
        { label: t('pix-transfer.new-transfer'), isActive: true },
    ];
    if (isLoading) {
        return (_jsx("div", { className: styles.layoutLoading, "data-testid": 'navbar-and-navcards-loading', children: _jsx(Loading, {}) }));
    }
    return (_jsxs("div", { className: styles.layoutRoot, "data-testid": 'navbar-and-navcards-layout', children: [breadcrumbs && breadcrumbs.length > 0 && isPaymentBilletRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: breadcrumbs, "data-testid": 'navbar-and-navcards-breadcrumb' })), pixKeyCreationBreadcrumbs && pixKeyCreationBreadcrumbs.length > 0 && isPixKeyCreationRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: pixKeyCreationBreadcrumbs, "data-testid": 'navbar-and-navcards-pix-breadcrumb' })), pixCopyPasteBreadcrumbs && pixCopyPasteBreadcrumbs.length > 0 && isPixCopyPasteRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: pixCopyPasteBreadcrumbs, "data-testid": 'vnavbar-and-navcards-pix-copy-paste-breadcrumb' })), pixTransferBreadcrumbs && pixTransferBreadcrumbs.length > 0 && isPixTransferRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: pixTransferBreadcrumbs, "data-testid": 'navbar-and-navcards-pix-transfer-breadcrumb' })), pixAccountPayableBreadcrumbs && pixAccountPayableBreadcrumbs.length > 0 && isPixAccountPayableRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: pixAccountPayableBreadcrumbs, "data-testid": 'navbar-and-navcards-pix-account-payable-breadcrumb' })), pixEmployeeAccountPayableBreadcrumbs &&
                pixEmployeeAccountPayableBreadcrumbs.length > 0 &&
                isPixEmployeeAccountPayableRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: pixEmployeeAccountPayableBreadcrumbs, "data-testid": 'navbar-and-navcards-pix-employee-account-payable-breadcrumb' })), pixTransferSuccessBreadcrumbs && pixTransferSuccessBreadcrumbs.length > 0 && isPixTransferSuccessRoute && (_jsx(Breadcrumb, { className: styles.breadcrumb, items: pixTransferSuccessBreadcrumbs, "data-testid": 'navbar-and-navcards-pix-transfer-success-breadcrumb' })), !isLoading &&
                activeAccount &&
                !isPaymentBilletRoute &&
                !isPixTransferRoute &&
                !isPixKeyCreationRoute &&
                !isPixCopyPasteRoute &&
                !isPixAccountPayableRoute &&
                !isPixEmployeeAccountPayableRoute &&
                !isChargesCreateRoute &&
                !isDepositBilletRoute &&
                !isChargesPDFRoute &&
                !isPixTransferSuccessRoute && _jsx(NavBar, { routes: navBarItems }), _jsxs("div", { className: styles.layoutContent, children: [_jsxs("div", { className: styles.accountLayoutContent, children: [!isLoading &&
                                !activeAccount &&
                                !isPaymentBilletRoute &&
                                !isPixKeyCreationRoute &&
                                !isPixCopyPasteRoute &&
                                !isPixAccountPayableRoute &&
                                !isPixEmployeeAccountPayableRoute &&
                                !isInstructionsRoute &&
                                !isPixTransferSuccessRoute && _jsx(DomainCards, { cards: domainCardItems, "data-testid": 'domain-cards' }), _jsxs("div", { className: styles.mainContent, "data-testid": 'navbar-and-navcards-main', children: [!isLoading && !activeAccount && _jsx(AccountActivationStatus, {}), !isLoading && activeAccount && props.children] })] }), !isInstructionsRoute && (_jsx("div", { className: styles.sideContent, "data-testid": 'navbar-and-navcards-side', children: _jsx(PaymentNavCardsAndCarousel, { isActive: true, productsData: productsData, domain: currentDomain === AppDomain.EMPRESA ? 'empresa' : 'escritorio', permissions: {
                                hasBillingPermission,
                                isBillingProductAvailable: !!getBillingProduct(),
                            } }) }))] })] }));
}
function RouterWrapper({ children }) {
    try {
        useLocation();
        return _jsx(_Fragment, { children: children });
    }
    catch {
        return _jsx(BrowserRouter, { children: children });
    }
}
export function DigitalAccountNavBarAndNavCardsLayout(props) {
    return (_jsx(RouterWrapper, { children: _jsx(TranslationProvider, { translations: sharedLayoutTranslations, defaultLanguage: 'pt-BR', children: _jsx(DigitalAccountNavBarAndNavCardsLayoutBase, { ...props }) }) }));
}
